// AppShell — Module Switcher + Contextual Sidebar
import { type ReactNode, useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Mail, Users, Building2, Clock, ScrollText, Database,
  UtensilsCrossed, LogOut, Truck, Recycle, Landmark, Package,
  BarChart3, AppWindow, Bell, CreditCard, ChevronDown,
  Sun, Moon, Radio, FileText, Flag, Webhook, AlertTriangle,
  Receipt, MapPin, Factory, Car, Layers, Satellite,
  Route, GitMerge, MessageSquare, BookOpen, ShieldCheck, Stethoscope,
} from 'lucide-react';
import { useAuth } from '../shared/auth/AuthContext';
import { useTheme } from '../shared/hooks/useTheme';

interface NavItem { href: string; icon: ReactNode; label: string; }
interface Module  { id: string; label: string; color: string; icon: ReactNode; basePaths: string[]; nav: NavItem[]; }

const MODULES: Module[] = [
  {
    id: 'platform', label: 'Platform', color: '#00c896',
    icon: <LayoutDashboard size={16} />, basePaths: ['/platform'],
    nav: [
      { href: '/platform/dashboard', icon: <LayoutDashboard size={15} />, label: 'Dashboard' },
      { href: '/platform/tenants',   icon: <Building2 size={15} />,       label: 'Tenants' },
      { href: '/platform/users',     icon: <Users size={15} />,           label: 'Users' },
      { href: '/platform/invites',   icon: <Mail size={15} />,            label: 'Invites' },
      { href: '/platform/audit',     icon: <ScrollText size={15} />,      label: 'Audit Log' },
    ],
  },
  {
    id: 'rehoreca', label: 'Rehoreca', color: '#22c55e',
    icon: <UtensilsCrossed size={16} />, basePaths: ['/rehoreca'],
    nav: [
      { href: '/rehoreca/activation-orders',     icon: <FileText size={15} />,   label: 'Activations' },
      { href: '/rehoreca/deactivation-requests', icon: <LogOut size={15} />,     label: 'Retreats' },
      { href: '/rehoreca/tenants',               icon: <Building2 size={15} />,  label: 'Tenants' },
      { href: '/rehoreca/submissions',           icon: <Layers size={15} />,     label: 'Submissions' },
      { href: '/rehoreca/organizations',         icon: <Landmark size={15} />,   label: 'Organizations' },
      { href: '/rehoreca/business-groups',       icon: <AppWindow size={15} />,  label: 'Business Groups' },
      { href: '/rehoreca/facilities',            icon: <Factory size={15} />,    label: 'Facilities' },
      { href: '/rehoreca/guest-nights',          icon: <Clock size={15} />,      label: 'Guest Nights' },
      { href: '/rehoreca/pylon-mapping',         icon: <MapPin size={15} />,     label: 'Pylon Mapping' },
      { href: '/rehoreca/pylon-clients',         icon: <Users size={15} />,      label: 'Pylon Clients' },
      { href: '/rehoreca/pylon-items',           icon: <Package size={15} />,    label: 'Pylon Items' },
      { href: '/rehoreca/pylon-log',             icon: <ScrollText size={15} />, label: 'Pylon Sync Log' },
      { href: '/rehoreca/label-batches',         icon: <Layers size={15} />,     label: 'QR Label Batches' },
      { href: '/rehoreca/certificate-diagnostics', icon: <Stethoscope size={15} />, label: 'Certificate Diagnostics' },
    ],
  },
  {
    id: 'hauler', label: 'Hauler', color: '#3b82f6',
    icon: <Truck size={16} />, basePaths: ['/hauler'],
    nav: [
      { href: '/hauler/tenants',    icon: <Building2 size={15} />, label: 'Tenants' },
      { href: '/hauler/contracts',  icon: <FileText size={15} />,  label: 'Contracts' },
      { href: '/hauler/facilities', icon: <Factory size={15} />,   label: 'Facilities' },
      { href: '/hauler/serviced-seds', icon: <Recycle size={15} />, label: 'Serviced ΣΕΔ' },
      { href: '/hauler/vehicles',   icon: <Truck size={15} />,     label: 'Vehicles' },
      { href: '/hauler/sessions',   icon: <Users size={15} />,     label: 'Sessions' },
      { href: '/hauler/movements',  icon: <Route size={15} />,     label: 'Movements' },
    ],
  },
  {
    id: 'city', label: 'City', color: '#f59e0b',
    icon: <Landmark size={16} />, basePaths: ['/city'],
    nav: [
      { href: '/city/municipalities', icon: <Landmark size={15} />, label: 'Municipalities' },
      { href: '/city/producers',      icon: <Factory size={15} />,  label: 'Producers' },
      { href: '/city/bins',           icon: <Package size={15} />,  label: 'Waste Bins' },
      { href: '/city/collections',    icon: <Recycle size={15} />,  label: 'Waste Collections' },
    ],
  },
  {
    id: 'wcr', label: 'WCR', color: '#a855f7',
    icon: <Recycle size={16} />, basePaths: ['/wcr', '/containers'],
    nav: [
      { href: '/wcr/dashboard',          icon: <LayoutDashboard size={15} />, label: 'Dashboard' },
      { href: '/wcr/ref-data',           icon: <Database size={15} />,        label: 'Reference Data' },
      { href: '/wcr/organizations',      icon: <Building2 size={15} />,       label: 'Organizations' },
      { href: '/wcr/facilities',         icon: <Factory size={15} />,         label: 'Facilities' },
      { href: '/wcr/vehicles',           icon: <Car size={15} />,             label: 'Vehicles' },
      { href: '/wcr/containers',         icon: <Package size={15} />,         label: 'Containers' },
      { href: '/wcr/beacons',            icon: <Satellite size={15} />,       label: 'Beacons' },
      { href: '/containers/batches',     icon: <Layers size={15} />,          label: 'Container Batches' },
      { href: '/containers/assignments', icon: <MapPin size={15} />,          label: 'Container Assignments' },
      { href: '/wcr/vehicle-claims',     icon: <AlertTriangle size={15} />,   label: 'Vehicle Claims' },
      { href: '/wcr/entity-match',       icon: <GitMerge size={15} />,        label: 'Entity Match' },
      { href: '/wcr/entity-aliases',     icon: <BookOpen size={15} />,        label: 'Alias Glossary' },
      { href: '/wcr/resolve-job-runs',   icon: <Clock size={15} />,           label: 'Resolve Job Runs' },
      { href: '/wcr/efodsa-rbac',        icon: <ShieldCheck size={15} />,     label: 'eFODSA Roles & Permissions' },
      { href: '/wcr/assets',             icon: <Factory size={15} />,         label: 'Assets (ανά Φορέα)' },
      { href: '/wcr/chat',               icon: <MessageSquare size={15} />,   label: 'WCR Chat (AI)' },
      { href: '/wcr/hma-sync',           icon: <Route size={15} />,           label: 'HMA Sync Log' },
      { href: '/wcr/audit-log',          icon: <ScrollText size={15} />,      label: 'Audit Log' },
    ],
  },
  {
    id: 'analytics', label: 'Analytics', color: '#6366f1',
    icon: <BarChart3 size={16} />, basePaths: ['/analytics'],
    nav: [
      { href: '/analytics',         icon: <BarChart3 size={15} />, label: 'Overview' },
      { href: '/analytics/reports', icon: <FileText size={15} />,  label: 'Reports' },
      { href: '/analytics/grafana', icon: <Radio size={15} />,     label: 'Grafana' },
    ],
  },
  {
    id: 'apps', label: 'App Registry', color: '#8b95ad',
    icon: <AppWindow size={16} />, basePaths: ['/apps'],
    nav: [
      { href: '/apps/registry',      icon: <AppWindow size={15} />, label: 'Registry' },
      { href: '/apps/feature-flags', icon: <Flag size={15} />,      label: 'Feature Flags' },
    ],
  },
  {
    id: 'notifications', label: 'Notifications', color: '#f97316',
    icon: <Bell size={16} />, basePaths: ['/notifications'],
    nav: [
      { href: '/notifications',          icon: <Bell size={15} />,          label: 'Campaigns' },
      { href: '/notifications/alerts',   icon: <AlertTriangle size={15} />, label: 'Alerts' },
      { href: '/notifications/webhooks', icon: <Webhook size={15} />,       label: 'Webhooks' },
    ],
  },
  {
    id: 'billing', label: 'Billing', color: '#eab308',
    icon: <CreditCard size={16} />, basePaths: ['/billing'],
    nav: [
      { href: '/billing',          icon: <CreditCard size={15} />, label: 'Subscriptions' },
      { href: '/billing/invoices', icon: <Receipt size={15} />,    label: 'Invoices' },
    ],
  },
];

function moduleFromPath(pathname: string): Module {
  return MODULES.find(m => m.basePaths.some(bp => pathname.startsWith(bp))) ?? MODULES[0];
}

function ModuleDropdown({ active, onSelect }: { active: Module; onSelect: (mod: Module) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="module-picker" ref={ref} style={{ position: 'relative' }}>
      <button className="module-trigger" onClick={() => setOpen(o => !o)}>
        <span className="module-trigger-dot" style={{ background: active.color, boxShadow: `0 0 6px ${active.color}80` }} />
        <span style={{ color: active.color, display: 'flex', alignItems: 'center' }}>{active.icon}</span>
        <span className="module-trigger-label">{active.label}</span>
        <ChevronDown size={14} className="module-trigger-chevron" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>
      {open && (
        <div className="module-dropdown">
          {MODULES.map(mod => (
            <div
              key={mod.id}
              className={`module-dropdown-item${mod.id === active.id ? ' active' : ''}`}
              onClick={() => { onSelect(mod); setOpen(false); }}
            >
              <span className="module-dropdown-dot" style={{ background: mod.color }} />
              <span style={{ color: mod.color, display: 'flex' }}>{mod.icon}</span>
              {mod.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RoleBadge({ roles }: { roles: string[] }) {
  const isAdmin    = roles.includes('identity-admin');
  const isOperator = roles.includes('identity-operator');
  const label = isAdmin ? 'Admin' : isOperator ? 'Operator' : 'Viewer';
  const color = isAdmin ? '#a855f7' : isOperator ? '#3b82f6' : '#6b7280';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      padding: '2px 7px', borderRadius: 10,
      background: color + '22', color, border: `1px solid ${color}44`,
    }}>
      {label}
    </span>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, doLogout } = useAuth();
  const location           = useLocation();
  const navigate           = useNavigate();
  const { theme, toggle }  = useTheme();

  const activeModule = moduleFromPath(location.pathname);
  const switchModule = (mod: Module) => navigate(mod.nav[0].href);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <div className="name">Waste<span>cloud</span></div>
          <div className="sub">Platform Admin</div>
        </div>

        <ModuleDropdown active={activeModule} onSelect={switchModule} />

        <nav className="nav">
          <div className="nav-context-label" style={{ color: activeModule.color }}>
            {activeModule.label}
          </div>
          {activeModule.nav.map(item => (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={({ isActive }) => isActive ? { color: activeModule.color, background: activeModule.color + '18' } : {}}
            >
              <span className="icon" style={{ display: 'flex', alignItems: 'center' }}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.email ?? '—'}
            </div>
            {user && <RoleBadge roles={user.roles ?? []} />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              onClick={toggle}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              style={{ background: 'none', border: '1px solid var(--border2)', borderRadius: 6, color: 'var(--muted)', cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center' }}
            >
              {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button
              onClick={doLogout}
              title="Logout"
              style={{ background: 'none', border: '1px solid var(--border2)', borderRadius: 6, color: 'var(--muted)', cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center' }}
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      <main className="shell-main">
        {children}
      </main>
    </div>
  );
}
